import React, { useMemo } from 'react';
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import ChartFrame, { TOOLTIP_STYLE } from './ChartFrame';
import { PMS_LABELS, fmtMoney, fmtNumber } from '../derive';

// Top hosts by orders in the active range. Compact horizontal bars on top,
// a slim 5-row leaderboard underneath. Click a row → host drawer.
export default function CanvasHosts({ filteredHosts, hostOrderStats, onSelectHost }) {
  const rows = useMemo(() => {
    return filteredHosts
      .map((h) => {
        const stats = hostOrderStats?.get(String(h._id)) || { orders: 0, revenue: 0, aov: 0 };
        return { ...h, orderCount: stats.orders, revenue: stats.revenue, aov: stats.aov };
      })
      .sort((a, b) => b.orderCount - a.orderCount)
      .slice(0, 8);
  }, [filteredHosts, hostOrderStats]);

  const chartData = rows.map((r) => ({
    name: (r.name || '?').split(' ')[0].slice(0, 12),
    orders: r.orderCount,
    _id: r._id,
  }));

  return (
    <div className="grid h-full min-h-0 grid-rows-[1fr_auto] gap-3">
      <ChartFrame
        title="Top hosts by orders"
        subtitle={`${fmtNumber(filteredHosts.length)} hosts in scope · click a bar for details`}
      >
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} layout="vertical" margin={{ top: 4, right: 8, bottom: 0, left: 8 }}>
            <XAxis type="number" tick={{ fontSize: 10, fill: '#71717a' }} allowDecimals={false} />
            <YAxis dataKey="name" type="category" tick={{ fontSize: 10, fill: '#71717a' }} width={70} />
            <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: 'rgba(99,102,241,0.06)' }} />
            <Bar
              dataKey="orders"
              fill="#6366f1"
              radius={[0, 4, 4, 0]}
              isAnimationActive={false}
              cursor="pointer"
              onClick={(b) => {
                const host = rows.find((r) => String(r._id) === String(b._id));
                if (host) onSelectHost?.(host);
              }}
            />
          </BarChart>
        </ResponsiveContainer>
      </ChartFrame>
      <div className="rounded-lg border border-zinc-200">
        <table className="w-full text-left text-[11px]">
          <thead className="bg-zinc-50 text-[9px] font-semibold uppercase tracking-wider text-zinc-500">
            <tr>
              <th className="px-2 py-1.5">Host</th>
              <th className="px-2 py-1.5">PMS</th>
              <th className="px-2 py-1.5 text-right">Orders</th>
              <th className="px-2 py-1.5 text-right">AOV</th>
              <th className="px-2 py-1.5 text-right">Volume</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100 bg-white">
            {rows.slice(0, 5).map((r) => (
              <tr
                key={r._id}
                onClick={() => onSelectHost?.(r)}
                className="cursor-pointer transition-colors duration-100 hover:bg-zinc-50 motion-reduce:transition-none"
              >
                <td className="px-2 py-1.5 font-semibold text-zinc-900">{r.name}</td>
                <td className="px-2 py-1.5 text-zinc-600">{PMS_LABELS[r.pmsType] || '—'}</td>
                <td className="px-2 py-1.5 text-right tabular-nums text-zinc-800">{fmtNumber(r.orderCount)}</td>
                <td className="px-2 py-1.5 text-right tabular-nums text-zinc-800">{fmtMoney(r.aov)}</td>
                <td className="px-2 py-1.5 text-right tabular-nums text-zinc-800">{fmtMoney(r.revenue)}</td>
              </tr>
            ))}
            {rows.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-2 py-3 text-center text-[11px] text-zinc-500">
                  No hosts in current scope.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}
